import { fetchJSON as fetchAPI } from './client';

export type Dependency = { key: string; id: string; name: string; type: string; status: string; description?: string };
export type Objective = { key: string; name: string; category: string; description?: string; mandatory: boolean };
export type StructureArea = { key: string; path: string; purpose: string; status: string; contains?: string[] };
export type FoundationComponent = { key: string; name: string; domain: string; description?: string; path?: string; status: string; depends_on?: string[] };
export type QualityGate = { key: string; name: string; command?: string; description?: string; blocking: boolean };
export type DeliverableItem = { key: string; name: string; category: string; description?: string; status: string };
export type OutOfScopeItem = { key: string; name: string; reason?: string; deferred_to?: string };
export type SuccessCriterion = { key: string; criterion: string; validation?: string; mandatory: boolean };

export type SprintDashboard = {
  sprint_id: string; tenant_id: string; version: string; priority: string; applies_to: string[];
  dependencies: number; objectives: number; structure_areas: number;
  backend_components: number; frontend_components: number; database_components: number;
  security_components: number; observability_items: number; cicd_items: number;
  testing_items: number; docker_items: number; documentation_items: number;
  quality_gates: number; deliverables: number; out_of_scope: number; success_criteria: number;
  generated_at: string;
};

export type SprintValidation = {
  valid: boolean; repository_builds: boolean; frontend_builds: boolean; backend_builds: boolean;
  docker_starts: boolean; migrations_execute: boolean; ci_passes: boolean; unit_tests_pass: boolean;
  health_endpoints_respond: boolean; auth_framework_compiles: boolean; observability_operational: boolean;
  feature_flags_operational: boolean; no_placeholders: boolean; gaps?: string[]; evaluated_at: string;
};

export type Dev100Status = {
  sprint_id: string; version: string; priority: string; applies_to: string[];
  dependencies: number; objectives: number; quality_gates: number; deliverables: number;
  sprint1_ready: boolean; production_ready: boolean; no_business_logic: boolean; status: string;
};

const GOVERNANCE_BASE = import.meta.env.VITE_GOVERNANCE_API ?? '/api/v1/governance';

function fetchGovernance<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${GOVERNANCE_BASE}${path}`, init);
}

export const repositoryBootstrapApi = {
  dashboard: () => fetchGovernance<SprintDashboard>('/dev100/dashboard'),
  dependencies: () => fetchGovernance<{ dependencies: Dependency[] }>('/dev100/dependencies').then((r) => r.dependencies),
  objectives: () => fetchGovernance<{ objectives: Objective[] }>('/dev100/objectives').then((r) => r.objectives),
  repositoryStructure: () => fetchGovernance<{ areas: StructureArea[] }>('/dev100/repository-structure').then((r) => r.areas),
  backendFoundation: () => fetchGovernance<{ components: FoundationComponent[] }>('/dev100/backend-foundation').then((r) => r.components),
  frontendFoundation: () => fetchGovernance<{ components: FoundationComponent[] }>('/dev100/frontend-foundation').then((r) => r.components),
  databaseFoundation: () => fetchGovernance<{ components: FoundationComponent[] }>('/dev100/database-foundation').then((r) => r.components),
  securityFoundation: () => fetchGovernance<{ components: FoundationComponent[] }>('/dev100/security-foundation').then((r) => r.components),
  observability: () => fetchGovernance<{ items: FoundationComponent[] }>('/dev100/observability').then((r) => r.items),
  cicd: () => fetchGovernance<{ items: FoundationComponent[] }>('/dev100/cicd').then((r) => r.items),
  testingFoundation: () => fetchGovernance<{ items: FoundationComponent[] }>('/dev100/testing-foundation').then((r) => r.items),
  docker: () => fetchGovernance<{ items: FoundationComponent[] }>('/dev100/docker').then((r) => r.items),
  documentation: () => fetchGovernance<{ items: FoundationComponent[] }>('/dev100/documentation').then((r) => r.items),
  qualityGates: () => fetchGovernance<{ gates: QualityGate[] }>('/dev100/quality-gates').then((r) => r.gates),
  deliverables: () => fetchGovernance<{ deliverables: DeliverableItem[] }>('/dev100/deliverables').then((r) => r.deliverables),
  outOfScope: () => fetchGovernance<{ items: OutOfScopeItem[] }>('/dev100/out-of-scope').then((r) => r.items),
  successCriteria: () => fetchGovernance<{ criteria: SuccessCriterion[] }>('/dev100/success-criteria').then((r) => r.criteria),
  validate: (attrs: Record<string, boolean>) =>
    fetchGovernance<SprintValidation>('/dev100/validate', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attrs }),
    }),
  status: () => fetchGovernance<Dev100Status>('/dev100/status'),
};
